import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLeague } from 'contexts/LeagueContext';
import { useAuth } from 'contexts/AuthContext';
import NavigationBar from 'components/ui/NavigationBar';
import Icon from 'components/AppIcon';

const ROLE_CONFIG = {
  owner: { label: 'Owner', color: 'bg-amber-100 text-amber-700 dark:bg-amber-950/30', icon: 'Crown' },
  admin: { label: 'Admin', color: 'bg-orange-100 text-orange-700 dark:bg-orange-950/30', icon: 'Shield' },
  member: { label: 'Member', color: 'bg-blue-100 text-blue-700 dark:bg-blue-950/30', icon: 'User' },
};

const MembersPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { activeLeague, leagueMembers, membersLoading, isOwner, isAdmin, userRole, updateMemberRole, removeMember } = useLeague();
  const [copiedCode, setCopiedCode] = useState(false);
  const [actionError, setActionError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);
  const [confirmRemove, setConfirmRemove] = useState(null);
  const [activeTab, setActiveTab] = useState('members');

  const handleCopyCode = async () => {
    try {
      await navigator.clipboard?.writeText(activeLeague?.code);
      setCopiedCode(true);
      setTimeout(() => setCopiedCode(false), 2000);
    } catch {}
  };

  const showSuccess = (msg) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(null), 3000);
  };

  const handleRoleChange = async (memberId, newRole) => {
    setActionError(null);
    try {
      await updateMemberRole(memberId, newRole);
      const roleLabel = newRole === 'admin' ? 'Admin' : 'Member';
      showSuccess(`Role updated to ${roleLabel}`);
    } catch (err) {
      setActionError(err?.message || 'Failed to update role');
    }
  };

  const handleRemoveMember = async (memberId) => {
    setActionError(null);
    const member = leagueMembers.find((m) => m.id === memberId);
    try {
      await removeMember(memberId);
      setConfirmRemove(null);
      showSuccess(`${member?.name || 'Member'} removed from league`);
    } catch (err) {
      setActionError(err?.message || 'Failed to remove member');
    }
  };

  const owners = leagueMembers.filter((m) => m.role === 'owner');
  const admins = leagueMembers.filter((m) => m.role === 'admin');
  const members = leagueMembers.filter((m) => m.role === 'member');

  const tabs = [
    { id: 'members', label: 'Members', icon: 'Users' },
    ...(isOwner ? [{ id: 'manage', label: 'Manage Roles', icon: 'ShieldCheck' }] : []),
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--color-background)' }}>
      <NavigationBar />
      <main className="main-content">
        <div className="px-4 md:px-6 lg:px-8 py-6 max-w-2xl mx-auto">
          {/* League Code Card */}
          <div
            className="rounded-xl border p-4 mb-6"
            style={{ backgroundColor: 'var(--color-card)', borderColor: 'var(--color-border)' }}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="font-caption text-xs font-medium mb-1" style={{ color: 'var(--color-muted-foreground)' }}>
                  League Invite Code
                </p>
                <p className="font-data text-2xl font-bold tracking-widest" style={{ color: 'var(--color-foreground)' }}>
                  {activeLeague?.code}
                </p>
              </div>
              <button
                onClick={handleCopyCode}
                className="flex items-center gap-2 px-4 py-2.5 rounded-lg font-caption text-sm font-medium transition-all"
                style={{
                  backgroundColor: copiedCode ? 'rgba(16,185,129,0.12)' : 'rgba(232,93,4,0.12)',
                  color: copiedCode ? '#059669' : 'var(--color-primary)',
                }}
              >
                <Icon name={copiedCode ? 'Check' : 'Copy'} size={16} color="currentColor" />
                {copiedCode ? 'Copied!' : 'Copy Code'}
              </button>
            </div>
            <p className="font-caption text-xs mt-2" style={{ color: 'var(--color-muted-foreground)' }}>
              Share this code with friends so they can join your league
            </p>
          </div>

          {/* Your Role */}
          <div
            className="rounded-xl border p-4 mb-6"
            style={{ backgroundColor: 'var(--color-card)', borderColor: 'var(--color-border)' }}
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(232,93,4,0.12)' }}>
                <Icon name={ROLE_CONFIG[userRole || 'member']?.icon || 'User'} size={20} color="var(--color-primary)" />
              </div>
              <div>
                <p className="font-caption text-xs" style={{ color: 'var(--color-muted-foreground)' }}>Your Role</p>
                <p className="font-heading font-semibold text-sm" style={{ color: 'var(--color-foreground)' }}>
                  {ROLE_CONFIG[userRole || 'member']?.label}
                </p>
              </div>
            </div>
          </div>

          {/* Tabs (only show if owner has manage tab) */}
          {tabs.length > 1 && (
            <div className="flex gap-1 mb-6 p-1 rounded-xl" style={{ backgroundColor: 'var(--color-muted)' }}>
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className="flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg font-caption text-sm font-medium transition-all"
                  style={{
                    backgroundColor: activeTab === tab.id ? 'var(--color-card)' : 'transparent',
                    color: activeTab === tab.id ? 'var(--color-foreground)' : 'var(--color-muted-foreground)',
                    boxShadow: activeTab === tab.id ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                  }}
                >
                  <Icon name={tab.icon} size={16} color="currentColor" />
                  {tab.label}
                </button>
              ))}
            </div>
          )}

          {actionError && (
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg mb-4 bg-red-50 dark:bg-red-950/20 border border-red-200">
              <Icon name="AlertCircle" size={14} color="#DC2626" />
              <span className="font-caption text-xs text-red-600">{actionError}</span>
            </div>
          )}

          {/* Members List Tab */}
          {activeTab === 'members' && (
            membersLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-8 h-8 border-4 rounded-full animate-spin" style={{ borderColor: 'var(--color-border)', borderTopColor: 'var(--color-primary)' }} />
              </div>
            ) : (
              <div className="space-y-4">
                {owners.length > 0 && (
                  <MemberSection title="Owner" members={owners} user={user} isOwner={isOwner} isAdmin={isAdmin} onRoleChange={handleRoleChange} onRemove={setConfirmRemove} onMessage={(userId) => navigate(`/messages/${userId}`)} />
                )}
                {admins.length > 0 && (
                  <MemberSection title="Admins" members={admins} user={user} isOwner={isOwner} isAdmin={isAdmin} onRoleChange={handleRoleChange} onRemove={setConfirmRemove} onMessage={(userId) => navigate(`/messages/${userId}`)} />
                )}
                {members.length > 0 && (
                  <MemberSection title="Members" members={members} user={user} isOwner={isOwner} isAdmin={isAdmin} onRoleChange={handleRoleChange} onRemove={setConfirmRemove} onMessage={(userId) => navigate(`/messages/${userId}`)} />
                )}

                {leagueMembers.length === 0 && (
                  <div className="text-center py-10 rounded-xl border" style={{ backgroundColor: 'var(--color-card)', borderColor: 'var(--color-border)' }}>
                    <Icon name="Users" size={36} color="var(--color-muted-foreground)" />
                    <p className="font-caption text-sm mt-3" style={{ color: 'var(--color-muted-foreground)' }}>
                      No members yet. Share the league code to invite people!
                    </p>
                  </div>
                )}
              </div>
            )
          )}

          {/* Manage Roles Tab (Owner only) */}
          {activeTab === 'manage' && isOwner && (
            <div className="space-y-3">
              <p className="font-caption text-xs mb-4" style={{ color: 'var(--color-muted-foreground)' }}>
                Set each member's role. Admins can record games and manage players. Members can only view.
              </p>
              {leagueMembers
                .filter((m) => m.userId !== user?.id)
                .map((member) => (
                  <RoleManageCard
                    key={member.id}
                    member={member}
                    onRoleChange={handleRoleChange}
                    onRemove={setConfirmRemove}
                  />
                ))}
              {leagueMembers.filter((m) => m.userId !== user?.id).length === 0 && (
                <div className="text-center py-10 rounded-xl border" style={{ backgroundColor: 'var(--color-card)', borderColor: 'var(--color-border)' }}>
                  <Icon name="UserPlus" size={36} color="var(--color-muted-foreground)" />
                  <p className="font-caption text-sm mt-3" style={{ color: 'var(--color-muted-foreground)' }}>
                    No other members to manage yet. Share the league code to invite people!
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </main>

      {/* Confirm Remove Modal */}
      {confirmRemove && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}>
          <div className="w-full max-w-sm rounded-2xl p-6" style={{ backgroundColor: 'var(--color-card)', boxShadow: 'var(--shadow-lg)' }}>
            <h3 className="font-heading font-bold text-lg mb-2" style={{ color: 'var(--color-foreground)' }}>Remove Member</h3>
            <p className="font-caption text-sm mb-4" style={{ color: 'var(--color-muted-foreground)' }}>
              Are you sure you want to remove <strong>{leagueMembers.find((m) => m.id === confirmRemove)?.name}</strong> from the league?
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setConfirmRemove(null)}
                className="flex-1 h-10 rounded-lg border font-caption text-sm font-medium"
                style={{ borderColor: 'var(--color-border)', color: 'var(--color-foreground)' }}
              >
                Cancel
              </button>
              <button
                onClick={() => handleRemoveMember(confirmRemove)}
                className="flex-1 h-10 rounded-lg font-caption text-sm font-medium bg-red-600 text-white hover:bg-red-700"
              >
                Remove
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success toast */}
      {successMsg && (
        <div
          className="fixed bottom-20 lg:bottom-6 left-1/2 -translate-x-1/2 z-[300] flex items-center gap-2 px-4 py-3 rounded-xl shadow-lg font-caption text-sm font-medium"
          style={{ backgroundColor: 'var(--color-success)', color: '#fff' }}
        >
          <Icon name="Check" size={16} color="#fff" />
          {successMsg}
        </div>
      )}
    </div>
  );
};

const MemberSection = ({ title, members, user, isOwner, isAdmin, onRoleChange, onRemove, onMessage }) => (
  <div>
    <h3 className="font-heading font-semibold text-sm mb-2 flex items-center gap-2" style={{ color: 'var(--color-muted-foreground)' }}>
      {title}
      <span className="font-caption text-xs font-normal px-1.5 py-0.5 rounded-full" style={{ backgroundColor: 'var(--color-muted)' }}>
        {members.length}
      </span>
    </h3>
    <div className="space-y-2">
      {members.map((member) => (
        <MemberCard
          key={member.id}
          member={member}
          isCurrentUser={member.userId === user?.id}
          isOwner={isOwner}
          isAdmin={isAdmin}
          onRoleChange={onRoleChange}
          onRemove={onRemove}
          onMessage={onMessage}
        />
      ))}
    </div>
  </div>
);

const MemberCard = ({ member, isCurrentUser, isOwner, isAdmin, onRoleChange, onRemove, onMessage }) => {
  const config = ROLE_CONFIG[member.role] || ROLE_CONFIG.member;
  const [showActions, setShowActions] = useState(false);

  const canManage = isOwner && !isCurrentUser && member.role !== 'owner';
  const canRemove = (isOwner || (isAdmin && member.role === 'member')) && !isCurrentUser && member.role !== 'owner';

  return (
    <div
      className="flex items-center gap-3 p-3 rounded-xl border transition-all"
      style={{ backgroundColor: 'var(--color-card)', borderColor: 'var(--color-border)' }}
    >
      {/* Avatar */}
      <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'var(--color-muted)' }}>
        <Icon name={config.icon} size={18} color="var(--color-muted-foreground)" />
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-heading font-semibold text-sm truncate" style={{ color: 'var(--color-foreground)' }}>
            {member.name}
          </span>
          {isCurrentUser && (
            <span className="font-caption text-xs px-1.5 py-0.5 rounded" style={{ backgroundColor: 'var(--color-muted)', color: 'var(--color-muted-foreground)' }}>
              You
            </span>
          )}
        </div>
        <span className={`inline-block px-2 py-0.5 rounded-full font-caption text-xs font-semibold mt-0.5 ${config.color}`}>
          {config.label}
        </span>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1 flex-shrink-0">
        {!isCurrentUser && (
          <button
            onClick={() => onMessage(member.userId)}
            className="p-2.5 rounded-lg hover:opacity-80 transition-opacity"
            style={{ backgroundColor: 'rgba(232,93,4,0.08)' }}
            title="Send message"
          >
            <Icon name="MessageSquare" size={16} color="var(--color-primary)" />
          </button>
        )}
        {(canManage || canRemove) && (
          <div className="relative">
            <button
              onClick={() => setShowActions(!showActions)}
              className="p-2.5 rounded-lg hover:opacity-80 transition-opacity"
              style={{ backgroundColor: 'var(--color-muted)' }}
            >
              <Icon name="MoreVertical" size={16} color="var(--color-muted-foreground)" />
            </button>
            {showActions && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setShowActions(false)} />
                <div
                  className="absolute right-0 top-full mt-1 z-20 w-44 rounded-lg border shadow-lg py-1"
                  style={{ backgroundColor: 'var(--color-card)', borderColor: 'var(--color-border)' }}
                >
                  {canManage && member.role === 'member' && (
                    <button
                      onClick={() => { onRoleChange(member.id, 'admin'); setShowActions(false); }}
                      className="w-full flex items-center gap-2 px-3 py-2 font-caption text-sm text-left hover:opacity-80"
                      style={{ color: 'var(--color-foreground)' }}
                    >
                      <Icon name="ShieldPlus" size={14} color="var(--color-primary)" />
                      Promote to Admin
                    </button>
                  )}
                  {canManage && member.role === 'admin' && (
                    <button
                      onClick={() => { onRoleChange(member.id, 'member'); setShowActions(false); }}
                      className="w-full flex items-center gap-2 px-3 py-2 font-caption text-sm text-left hover:opacity-80"
                      style={{ color: 'var(--color-foreground)' }}
                    >
                      <Icon name="ShieldMinus" size={14} color="var(--color-muted-foreground)" />
                      Demote to Member
                    </button>
                  )}
                  {canRemove && (
                    <button
                      onClick={() => { onRemove(member.id); setShowActions(false); }}
                      className="w-full flex items-center gap-2 px-3 py-2 font-caption text-sm text-left text-red-600"
                    >
                      <Icon name="UserMinus" size={14} color="#DC2626" />
                      Remove from League
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const RoleManageCard = ({ member, onRoleChange, onRemove }) => {
  const config = ROLE_CONFIG[member.role] || ROLE_CONFIG.member;
  const [updating, setUpdating] = useState(false);

  const handleRoleSelect = async (newRole) => {
    if (newRole === member.role) return;
    setUpdating(true);
    try {
      await onRoleChange(member.id, newRole);
    } finally {
      setUpdating(false);
    }
  };

  return (
    <div
      className="rounded-xl border p-4"
      style={{ backgroundColor: 'var(--color-card)', borderColor: 'var(--color-border)' }}
    >
      <div className="flex items-center gap-3 mb-3">
        <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'var(--color-muted)' }}>
          <Icon name={config.icon} size={18} color="var(--color-muted-foreground)" />
        </div>
        <div className="flex-1 min-w-0">
          <span className="font-heading font-semibold text-sm truncate block" style={{ color: 'var(--color-foreground)' }}>
            {member.name}
          </span>
          <span className={`inline-block px-2 py-0.5 rounded-full font-caption text-xs font-semibold mt-0.5 ${config.color}`}>
            {config.label}
          </span>
        </div>
        <button
          onClick={() => onRemove(member.id)}
          className="p-2 rounded-lg hover:opacity-80 transition-opacity"
          style={{ backgroundColor: 'rgba(220,38,38,0.08)' }}
          title="Remove member"
        >
          <Icon name="UserMinus" size={16} color="#DC2626" />
        </button>
      </div>

      {/* Role selector buttons */}
      <div className="flex gap-2">
        {['admin', 'member'].map((role) => {
          const rc = ROLE_CONFIG[role];
          const isActive = member.role === role;
          return (
            <button
              key={role}
              onClick={() => handleRoleSelect(role)}
              disabled={updating}
              className={`flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg font-caption text-sm font-medium transition-all border ${
                updating ? 'opacity-50 cursor-not-allowed' : ''
              }`}
              style={{
                backgroundColor: isActive ? 'rgba(232,93,4,0.12)' : 'transparent',
                borderColor: isActive ? 'var(--color-primary)' : 'var(--color-border)',
                color: isActive ? 'var(--color-primary)' : 'var(--color-muted-foreground)',
              }}
            >
              <Icon name={rc.icon} size={14} color="currentColor" />
              {rc.label}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default MembersPage;
